import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n/I18nContext';
import { AppHeader } from '../components/AppHeader';
import { SudokuBoard } from '../components/SudokuBoard';
import { Numpad } from '../components/Numpad';
import { formatTime } from '../hooks/useGameBoard';
import { setCoinBalance } from '../components/CoinChip';
import './GamePage.css'; // Numpad (.num-btn/.number-pad) burada tanımlı — diğer tüm oyun sayfaları da import eder
import './TournamentsPage.css';
import './TournamentPlayPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

type Cell = number | null;
type Grid = Cell[][];

interface PuzzleData {
  tournament_id: number;
  puzzle: Grid;
  difficulty: number;
  starts_at: string;
  ends_at: string;
}

function cloneGrid(g: Grid): Grid {
  return g.map(row => row.slice());
}

function emptyBool(): boolean[][] {
  return Array.from({ length: 9 }, () => Array(9).fill(false));
}

function findConflicts(board: Grid): boolean[][] {
  const conflicts = emptyBool();
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const v = board[r][c];
      if (v == null) continue;
      // row
      for (let c2 = 0; c2 < 9; c2++) {
        if (c2 !== c && board[r][c2] === v) conflicts[r][c] = true;
      }
      // col
      for (let r2 = 0; r2 < 9; r2++) {
        if (r2 !== r && board[r2][c] === v) conflicts[r][c] = true;
      }
      // box
      const br = Math.floor(r / 3) * 3;
      const bc = Math.floor(c / 3) * 3;
      for (let dr = 0; dr < 3; dr++) {
        for (let dc = 0; dc < 3; dc++) {
          const r2 = br + dr, c2 = bc + dc;
          if ((r2 !== r || c2 !== c) && board[r2][c2] === v) conflicts[r][c] = true;
        }
      }
    }
  }
  return conflicts;
}

function isComplete(board: Grid): boolean {
  return board.every(row => row.every(value => value != null))
    && !findConflicts(board).some(row => row.some(Boolean));
}

export function TournamentPlayPage() {
  const { id } = useParams<{ id: string }>();
  const { user, token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [puzzle, setPuzzle] = useState<PuzzleData | null>(null);
  const [board, setBoard] = useState<Grid | null>(null);
  const [original, setOriginal] = useState<Grid | null>(null);
  const [selected, setSelected] = useState<{ r: number; c: number } | null>(null);
  const [mistakes, setMistakes] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submittedScore, setSubmittedScore] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    const tid = Number(id);
    if (!tid) {
      navigate('/tournaments');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/tournaments/${tid}/puzzle`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail ?? 'load_failed');
          return;
        }
        const data: PuzzleData = await res.json();
        setPuzzle(data);
        setBoard(cloneGrid(data.puzzle));
        setOriginal(cloneGrid(data.puzzle));
        startedAt.current = Date.now();
      } catch {
        setError('network_error');
      }
    })();
  }, [id, user, token, navigate]);

  // Timer
  useEffect(() => {
    if (!puzzle || submitted) return;
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [puzzle, submitted]);

  const handleCellChange = async (r: number, c: number, value: number | null) => {
    if (!board || !original || !puzzle || submitted || !token) return;
    if (original[r][c] !== null) return;
    const res = await fetch(`${API}/tournaments/${puzzle.tournament_id}/move`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ row: r, col: c, value }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.detail ?? 'move_failed');
      return;
    }
    setMistakes(data.mistakes ?? mistakes);
    if (data.correct) {
      setBoard(current => {
        if (!current) return current;
        const next = cloneGrid(current);
        next[r][c] = value;
        return next;
      });
    }
  };

  const handleNumber = (n: number) => {
    if (!selected || !board || submitted) return;
    handleCellChange(selected.r, selected.c, n);
  };

  const submitNow = useCallback(async () => {
    if (!puzzle || !board || !isComplete(board) || submitting || submitted || !token) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/tournaments/${puzzle.tournament_id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setSubmitted(true);
        setSubmittedScore(data.score);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail ?? 'submit_failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [puzzle, board, token, submitting, submitted]);

  // Auto submit on completion
  useEffect(() => {
    if (!board || !puzzle || submitted) return;
    if (isComplete(board)) {
      submitNow();
    }
  }, [board, puzzle, submitted, submitNow]);

  if (error) {
    return (
      <div className="tournaments-page">
        <AppHeader />
        <div className="tournaments-container">
          <div className="tournaments-empty">
            <div className="tournaments-empty-icon">⚠️</div>
            <p>{(t.tournaments as any)?.[`error_${error}`] ?? error}</p>
            <button className="btn-primary" onClick={() => navigate('/tournaments')}>
              {t.tournaments.backToList}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle || !board || !original) {
    return (
      <div className="tournaments-page">
        <AppHeader />
        <div className="tournaments-loading">{t.common.loading}</div>
      </div>
    );
  }

  const conflicts = findConflicts(board);
  const uncertain = emptyBool();

  return (
    <div className="tournaments-page">
      <AppHeader showFontToggle />
      <div className="tn-play-container">
        <div className="tn-play-header">
          <div>
            <div className="tn-play-label">{t.tournaments.tournament}</div>
            <div className="tn-play-name">#{puzzle.tournament_id}</div>
          </div>
          <div className="tn-play-stats">
            <div className="tn-stat">
              <div className="tn-stat-label">{t.tournaments.time}</div>
              <div className="tn-stat-value">{formatTime(elapsed)}</div>
            </div>
            <div className="tn-stat">
              <div className="tn-stat-label">{t.tournaments.mistakes}</div>
              <div className="tn-stat-value mistakes">{mistakes}</div>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="tn-play-finish">
            <div className="tn-finish-icon">🏆</div>
            <h2>{t.tournaments.submitted}</h2>
            <div className="tn-finish-score">{t.tournaments.pointsValue.replace('{points}', String(submittedScore))}</div>
            <div className="tn-finish-meta">
              {formatTime(elapsed)} · {mistakes} {t.tournaments.mistakes.toLowerCase()}
            </div>
            <button className="btn-primary" onClick={() => navigate('/tournaments')}>
              {t.tournaments.backToList}
            </button>
          </div>
        ) : (
          <>
            <div className="tn-board-wrap">
              <SudokuBoard
                board={board}
                originalBoard={original}
                uncertain={uncertain}
                conflicts={conflicts}
                onChange={(r, c, v) => {
                  setSelected({ r, c });
                  handleCellChange(r, c, v);
                }}
                highlightTarget={selected ? `${selected.r}-${selected.c}` : null}
                highlightRelated={new Set()}
              />
            </div>
            <div className="tn-play-numpad">
              <Numpad
                board={board}
                highlightDigit={selected ? board[selected.r][selected.c] : null}
                onNumberClick={handleNumber}
              />
            </div>
            <div className="tn-play-actions">
              <button
                className="btn-secondary"
                onClick={() => navigate('/tournaments')}
                disabled={submitting}
              >
                {t.tournaments.giveUp}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
// CoinChip helper unused but referenced for type consistency
void setCoinBalance;
